import {
  COMPANY_ADDRESS_FULL,
  COMPANY_LOCATION,
  COMPANY_MONOGRAM,
  COMPANY_NAME,
  COMPANY_REGION,
  COMPANY_SHORT,
  MAP_URL,
  FOCUS_ROWS,
  MISSION_BENTO,
  PROFILE_FACTS,
  PUBLIC_INFO,
} from './constants';

const TONE_STYLES = {
  forest: 'bg-forest text-cream',
  moss: 'bg-moss text-cream',
  clay: 'bg-clay text-cream',
  amber: 'bg-amber text-forest',
  sand: 'bg-sand text-forest border border-divider',
  cream: 'bg-cream text-forest border border-divider',
};

function Header() {
  const navItems = [
    { label: 'Profile', href: '#profile' },
    { label: 'About', href: '#about' },
    { label: 'Focus', href: '#focus' },
    { label: 'Location', href: '#location' },
  ];

  return (
    <header className="sticky top-0 z-30 border-b border-divider/70 bg-sand/90 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4">
        <a href="#top" className="flex min-w-0 items-center gap-3" aria-label={`${COMPANY_SHORT} home`}>
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-forest text-xs font-black tracking-wider text-amber">
            {COMPANY_MONOGRAM}
          </span>
          <span className="hidden font-display text-base font-bold tracking-tight text-forest sm:inline">
            {COMPANY_SHORT}
          </span>
        </a>
        <nav className="hidden items-center gap-1 rounded-full border border-divider bg-white/70 px-2 py-1 text-sm font-semibold text-forest md:flex" aria-label="Primary">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="rounded-full px-3 py-1.5 transition hover:bg-forest hover:text-cream"
            >
              {item.label}
            </a>
          ))}
        </nav>
        <a
          href="#location"
          className="rounded-full bg-forest px-4 py-2 text-sm font-bold text-cream transition hover:bg-clay"
        >
          View Address
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="profile" className="relative overflow-hidden border-b border-divider bg-sand">
      <div className="mx-auto max-w-5xl px-5 py-16 text-center lg:py-24">
        <p className="inline-flex items-center gap-2 rounded-full border border-divider bg-white/70 px-3 py-1 text-[11px] font-black uppercase tracking-[0.22em] text-clay">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-clay" />
          {COMPANY_REGION}
        </p>
        <h1 className="mt-6 font-display text-5xl font-black leading-[1.05] tracking-tight text-forest sm:text-6xl lg:text-7xl">
          {COMPANY_NAME}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-stone">
          A quiet public profile for the Wintersville-based community organization. The page lists only directory-safe
          information — name, region, and mailing address.
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href="#about"
            className="rounded-full bg-forest px-6 py-3 font-bold text-cream shadow-soft transition hover:bg-clay"
          >
            Read the profile
          </a>
          <a
            href="#location"
            className="rounded-full border border-forest/30 bg-white/70 px-6 py-3 font-bold text-forest transition hover:bg-forest hover:text-cream"
          >
            Jump to address
          </a>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-5 pb-16 lg:pb-24">
        <div className="hero-panel grain relative overflow-hidden rounded-3xl border border-forest/30 p-8 text-cream shadow-soft sm:p-12">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.28em] text-amber">Mailing Address</p>
              <p className="mt-4 font-display text-3xl font-black leading-tight sm:text-4xl">{COMPANY_ADDRESS_FULL}</p>
              <p className="mt-4 max-w-md text-cream/75">
                Use the address above for public directory reference and correspondence.
              </p>
            </div>
            <div className="rounded-2xl border border-amber/30 bg-forest/40 p-6 backdrop-blur">
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-amber/90">Locale</p>
              <p className="mt-3 font-display text-2xl font-black">{COMPANY_LOCATION}</p>
              <p className="mt-4 text-sm text-cream/70">
                Eastern Ohio River corridor — Jefferson County, ZIP 43953.
              </p>
              <a
                href={MAP_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-5 inline-flex rounded-full bg-amber px-4 py-2 text-sm font-bold text-forest transition hover:bg-cream"
              >
                Open map
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FactsStrip() {
  return (
    <section aria-label="Profile facts" className="border-b border-divider bg-cream">
      <div className="mx-auto grid max-w-6xl gap-px overflow-hidden rounded-none bg-divider px-0 sm:grid-cols-2 lg:grid-cols-4">
        {PROFILE_FACTS.map((fact) => (
          <div key={fact.label} className="bg-cream px-6 py-7">
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-clay">{fact.label}</p>
            <p className="mt-2 font-display text-xl font-black leading-snug text-forest">{fact.value}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function MissionBento() {
  return (
    <section id="about" className="bg-sand">
      <div className="mx-auto max-w-6xl px-5 py-16 lg:py-24">
        <div className="mb-10 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-clay">Profile Overview</p>
            <h2 className="mt-3 font-display text-3xl font-black text-forest sm:text-4xl">
              What this page shows — and what it does not
            </h2>
          </div>
          <p className="max-w-sm text-sm font-semibold uppercase tracking-[0.18em] text-stone">
            Directory-safe content only
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-6 lg:auto-rows-[minmax(140px,auto)]">
          {MISSION_BENTO.map((card) => (
            <article
              key={card.title}
              className={`flex flex-col justify-between rounded-2xl p-6 ${card.span} ${TONE_STYLES[card.tone]}`}
            >
              <p className="text-[11px] font-black uppercase tracking-[0.22em] opacity-80">{card.eyebrow}</p>
              <div className="mt-6">
                <h3 className="font-display text-2xl font-black leading-snug">{card.title}</h3>
                <p className="mt-3 text-sm leading-7 opacity-90">{card.body}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FocusZigzag() {
  return (
    <section id="focus" className="bg-cream">
      <div className="mx-auto max-w-6xl px-5 py-16 lg:py-24">
        <div className="mb-12 max-w-2xl">
          <p className="text-[11px] font-black uppercase tracking-[0.22em] text-clay">Focus</p>
          <h2 className="mt-3 font-display text-3xl font-black text-forest sm:text-4xl">
            Four notes about this profile
          </h2>
          <p className="mt-4 leading-7 text-stone">
            The page is intentionally short. Each note explains a small editorial choice rather than promoting a program.
          </p>
        </div>

        <ol className="relative space-y-6 border-l-2 border-divider pl-6 sm:space-y-8 sm:pl-10">
          {FOCUS_ROWS.map((row, idx) => (
            <li
              key={row.number}
              className={`relative ${idx % 2 === 1 ? 'sm:ml-12' : ''}`}
            >
              <span className="absolute -left-[34px] top-2 grid h-7 w-7 place-items-center rounded-full bg-forest font-display text-xs font-black text-amber sm:-left-[50px] sm:h-8 sm:w-8 sm:text-sm">
                {row.number}
              </span>
              <div className="rounded-2xl border border-divider bg-white p-6 shadow-ring transition hover:-translate-y-0.5 hover:shadow-soft">
                <h3 className="font-display text-xl font-black text-forest sm:text-2xl">{row.title}</h3>
                <p className="mt-3 leading-7 text-stone">{row.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-12 grid gap-4 md:grid-cols-3">
          {PUBLIC_INFO.map((item) => (
            <div key={item.label} className="rounded-2xl border border-divider bg-sand p-5">
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-clay">{item.label}</p>
              <p className="mt-2 font-display text-lg font-black leading-snug text-forest">{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocationBand() {
  return (
    <section id="location" className="bg-forest text-cream">
      <div className="mx-auto max-w-3xl px-5 py-20 text-center lg:py-24">
        <p className="text-[11px] font-black uppercase tracking-[0.28em] text-amber">Location</p>
        <h2 className="mt-3 font-display text-3xl font-black sm:text-4xl">{COMPANY_LOCATION}</h2>
        <p className="mx-auto mt-4 max-w-xl leading-7 text-cream/70">
          Public reference address for directory and correspondence use.
        </p>
        <div className="mx-auto mt-10 max-w-xl rounded-3xl border border-amber/25 bg-moss/40 p-8 backdrop-blur">
          <p className="text-[11px] font-black uppercase tracking-[0.22em] text-amber/85">Organization Address</p>
          <p className="mt-4 font-display text-2xl font-black leading-snug">{COMPANY_ADDRESS_FULL}</p>
          <a
            href={MAP_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-7 inline-flex rounded-full bg-amber px-6 py-3 font-bold text-forest transition hover:bg-cream"
          >
            Open map
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-charcoal text-cream/70">
      <div className="mx-auto flex max-w-6xl flex-col gap-3 px-5 py-8 text-sm md:flex-row md:items-center md:justify-between">
        <p className="font-bold text-cream">
          &copy; {new Date().getFullYear()} {COMPANY_NAME}
        </p>
        <p>{COMPANY_REGION}</p>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div id="top" className="min-h-screen bg-cream text-charcoal">
      <a
        href="#about"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-forest focus:px-3 focus:py-2 focus:text-cream"
      >
        Skip to content
      </a>
      <Header />
      <main>
        <Hero />
        <FactsStrip />
        <MissionBento />
        <FocusZigzag />
        <LocationBand />
      </main>
      <Footer />
    </div>
  );
}

export default App;
